import React from 'react';
import { X, AlertCircle, DollarSign, Package, Settings, Users, Bell } from 'lucide-react';

const RealTimeNotifications = ({ 
  notifications, 
  removeNotification, 
  clearNotifications,
  connectionStatus 
}) => {
  
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'alarm': return <AlertCircle size={20} className="text-red-500" />;
      case 'accounting': return <DollarSign size={20} className="text-green-500" />;
      case 'order': return <Package size={20} className="text-blue-500" />;
      case 'admin': return <Settings size={20} className="text-purple-500" />;
      case 'user': return <Users size={20} className="text-gray-500" />;
      default: return <Bell size={20} className="text-gray-400" />;
    }
  };
  
  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      case 'normal': return 'border-blue-500 bg-blue-50';
      default: return 'border-gray-300 bg-white';
    }
  };
  
  const getConnectionIndicator = () => {
    switch (connectionStatus) {
      case 'connected':
        return <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" title="Tiempo real activo" />;
      case 'connecting':
        return <div className="w-3 h-3 bg-yellow-500 rounded-full animate-spin" title="Conectando..." />;
      case 'disconnected':
        return <div className="w-3 h-3 bg-red-500 rounded-full" title="Desconectado" />;
      case 'error':
        return <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" title="Error de conexión" />;
      default:
        return <div className="w-3 h-3 bg-gray-400 rounded-full" title="Estado desconocido" />;
    }
  };
  
  if (notifications.length === 0) {
    return (
      <div className="fixed top-4 right-4 z-50">
        {/* Indicador de conexión */}
        <div className="bg-white rounded-lg shadow-md px-3 py-2 flex items-center gap-2 text-sm">
          {getConnectionIndicator()}
          <span className="text-gray-600">
            {connectionStatus === 'connected' ? 'Tiempo Real' : 'Sin conexión'}
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full space-y-2">
      {/* Header con indicador de conexión */}
      <div className="bg-white rounded-lg shadow-md px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getConnectionIndicator()}
          <span className="text-sm font-medium text-gray-700">
            Notificaciones ({notifications.length})
          </span>
        </div>
        
        {notifications.length > 1 && (
          <button
            onClick={clearNotifications}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            Limpiar todo
          </button>
        )}
      </div>
      
      {/* Lista de notificaciones */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`bg-white rounded-lg shadow-lg border-l-4 p-4 transition-all duration-300 ${getNotificationColor(notification.priority)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                {getNotificationIcon(notification.type)}
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm">
                    {notification.title}
                  </h4>
                  <p className="text-gray-700 text-sm mt-1">
                    {notification.message}
                  </p>
                  
                  {/* Información adicional para alarmas críticas */}
                  {notification.priority === 'critical' && notification.data && (
                    <div className="mt-2 p-2 bg-red-100 rounded text-xs">
                      <div><strong>Bodega:</strong> {notification.data.bodega}</div>
                      <div><strong>Creado por:</strong> {notification.data.createdBy}</div>
                    </div>
                  )}
                  
                  {/* Timestamp */}
                  <div className="text-xs text-gray-500 mt-2">
                    {new Date().toLocaleTimeString('es-CO', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => removeNotification(notification.id)}
                className="text-gray-400 hover:text-gray-600 ml-2"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RealTimeNotifications;